/* eslint-disable react/no-unstable-nested-components */
import {
    ActionButton,
    AlertDialog,
    AlertDialogContent,
    Badge,
    Button,
    Card,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { useSearchParams, Link } from "react-router-dom";
import { differenceInCalendarDays, format } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import ListState from "../../../../components/ListState/ListState";
import { useDebounce } from "../../../../hooks/helpers";
import {
    displayNameInRightFormat,
    formatDate,
    convertDateFilterStringToDate,
    cn,
    removeEnumUnderscore,
    makeStringFirstLetterCapital,
    handleDisplayClientEmailCorrectly,
    appendMidnightTimeToDate,
    formatExpiryStatus,
} from "../../../../utils/helpers";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import Navbar from "../../../../components/Navbar/Navbar";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import useToast from "../../../../hooks/useToast";
import EyeIcon from "../../../../icons/Eye";
import EditIcon from "../../../../icons/Edit";
import CheckIcon from "../../../../icons/Check";
import TimesIcon from "../../../../icons/Times";
import DeleteIcon from "../../../../icons/Delete";
import UploadIcon from "../../../../icons/Upload";
import CreateClientFormForm from "../CreateClientFormForm/CreateClientFormForm";
import UploadFormDocumentForm from "../UploadFormDocumentForm/UploadFormDocumentForm";
import {
    useUpdateClientForm,
    useDeleteClientForm,
} from "../../hooks/mutations/client-forms.mutations";
import { useGetClientForms } from "../../hooks/queries/client-forms.queries";
import {
    FormStatus,
    FormType,
    IClientForm,
    IFormDocument,
    IUpdateClientFormPayload,
} from "../../types/client-forms.types";
import Loader from "../../../../shared-ui/Loader/Loader";
import DocumentViewer from "../../../../components/DocumentViewer/DocumentViewer";
import {
    CLIENT_FORM_STATUSES,
    getStatusDisplay,
    getStatusTag,
} from "../../helpers/client-forms.helpers";
import FormActivityLog from "../FormActivityLog/FormActivityLog";
import {
    useDoesUserHavePermission,
    useDoesUserHaveRole,
} from "../../../../hooks/permissions";
import { UserPermisions, UserRole } from "../../../../utils/types/user";

type Dialogs = "activity" | "create" | "upload" | "delete";

export default function ClientForms() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    const [selectedForm, setSelectedForm] = React.useState<IClientForm | null>(
        null
    );
    const [showDocumentViewer, setShowDocumentViewer] = React.useState(false);
    const [selectedDocuments, setSelectedDocuments] = React.useState<
        IFormDocument[]
    >([]);
    const [currentDocumentIndex, setCurrentDocumentIndex] = React.useState(0);

    const [editingFormId, setEditingFormId] = React.useState<string | null>(
        null
    );
    const [editedStartDate, setEditedStartDate] = React.useState<Date | null>(
        null
    );
    const [editedEndDate, setEditedEndDate] = React.useState<Date | null>(null);
    const [editedSignedDate, setEditedSignedDate] = React.useState<Date | null>(
        null
    );

    const { toast } = useToast();
    const queryClient = useQueryClient();
    const updateFormMutation = useUpdateClientForm();
    const deleteFormMutation = useDeleteClientForm();

    const hasClientFormWritePermission = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_WRITE,
        UserPermisions.CLIENT_INFO_PERSONAL_WRITE,
    ]);
    const hasClientFormRole = useDoesUserHaveRole([
        UserRole.SUPER_ADMIN,
        UserRole.CLIENT_SUPPORT,
        UserRole.SUPPORT_GENERAL,
    ]);
    const canWriteClientForms =
        hasClientFormWritePermission && hasClientFormRole;

    const searchString = searchParams.get("search") || "";
    const statusFilter = (searchParams.get("status") as FormStatus) || "";
    const formTypeFilter = searchParams.get("form_type") || "";
    const startDateFromFilter = searchParams.get("start_date_from");
    const startDateToFilter = searchParams.get("start_date_to");
    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;

    const search = useDebounce(searchString, 500);

    const { data, error, isLoading, isSuccess } = useGetClientForms({
        search_string: search,
        status: (statusFilter as FormStatus) || undefined,
        form_type: (formTypeFilter as FormType) || undefined,
        start_date_from: startDateFromFilter || undefined,
        start_date_to: startDateToFilter || undefined,
        page: pageFilter,
        limit: limitFilter,
        order_by: "end_date:ASC",
    });

    const handleSetParams = (key: string, value: string) => {
        if (value === "") searchParams.delete(key);
        else searchParams.set(key, value);
        searchParams.set("page", "1");
        localStorage.setItem("clientforms_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        localStorage.setItem("clientforms_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        localStorage.setItem("clientforms_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleViewDocuments = React.useCallback(
        (documents: IFormDocument[]) => {
            if (documents.length > 0) {
                setSelectedDocuments(documents);
                setCurrentDocumentIndex(0);
                setShowDocumentViewer(true);
            }
        },
        []
    );

    const handleCloseDocumentViewer = () => {
        setShowDocumentViewer(false);
        setSelectedDocuments([]);
        setCurrentDocumentIndex(0);
    };

    const handleEditForm = React.useCallback((form: IClientForm) => {
        setEditingFormId(form.form_id);
        setEditedStartDate(
            form.start_date
                ? convertDateFilterStringToDate(form.start_date)
                : null
        );
        setEditedEndDate(
            form.end_date ? convertDateFilterStringToDate(form.end_date) : null
        );
        setEditedSignedDate(
            form.signed_at
                ? convertDateFilterStringToDate(form.signed_at)
                : null
        );
    }, []);

    const handleCancelEdit = () => {
        setEditingFormId(null);
        setEditedStartDate(null);
        setEditedEndDate(null);
        setEditedSignedDate(null);
    };

    const handleSaveEdit = React.useCallback(
        (form: IClientForm) => {
            const updateData: IUpdateClientFormPayload = {};
            const origStart = form.start_date?.split("T")[0] || "";
            const origEnd = form.end_date?.split("T")[0] || "";
            const origSigned = form.signed_at?.split("T")[0] || "";
            const newStart = editedStartDate
                ? editedStartDate.toISOString().split("T")[0]
                : "";
            const newEnd = editedEndDate
                ? editedEndDate.toISOString().split("T")[0]
                : "";
            const newSigned = editedSignedDate
                ? editedSignedDate.toISOString().split("T")[0]
                : "";

            if (newStart && newStart !== origStart)
                updateData.start_date = newStart;
            if (newEnd && newEnd !== origEnd) updateData.end_date = newEnd;
            if (newSigned !== origSigned)
                updateData.signed_at = newSigned || null;

            if (Object.keys(updateData).length === 0) {
                handleCancelEdit();
                return;
            }

            updateFormMutation.mutate(
                {
                    clientId: form.client_id,
                    formId: form.form_id,
                    data: updateData,
                },
                {
                    onSuccess: () => {
                        queryClient.invalidateQueries(["client-forms"]);
                        toast({
                            mode: "success",
                            message: "Form updated successfully",
                        });
                        handleCancelEdit();
                    },
                    onError: (err) => {
                        toast({
                            mode: "error",
                            message:
                                err.response?.data.message ||
                                "Could not update form",
                        });
                    },
                }
            );
        },
        [
            updateFormMutation,
            queryClient,
            toast,
            editedStartDate,
            editedEndDate,
            editedSignedDate,
        ]
    );

    const handleChangeStatus = React.useCallback(
        (form: IClientForm, status: FormStatus) => {
            updateFormMutation.mutate(
                {
                    clientId: form.client_id,
                    formId: form.form_id,
                    data: { status },
                },
                {
                    onSuccess: (response) => {
                        queryClient.invalidateQueries(["client-forms"]);
                        toast({
                            mode: "success",
                            message: response.message || "Status updated",
                        });
                    },
                    onError: (err) => {
                        toast({
                            mode: "error",
                            message:
                                err.response?.data.message ||
                                "Could not update status",
                        });
                    },
                }
            );
        },
        [updateFormMutation, queryClient, toast]
    );

    const handleDeleteForm = () => {
        if (!selectedForm) return;
        deleteFormMutation.mutate(
            {
                clientId: selectedForm.client_id,
                formId: selectedForm.form_id,
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries(["client-forms"]);
                    toast({
                        mode: "success",
                        message: "Form deleted successfully",
                    });
                    setDialogInView(null);
                    setSelectedForm(null);
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data?.message ||
                            "Could not delete form",
                    });
                },
            }
        );
    };

    const startDateFromFilterToDate =
        convertDateFilterStringToDate(startDateFromFilter);
    const startDateToFilterToDate =
        convertDateFilterStringToDate(startDateToFilter);

    const columns: ColumnDef<IClientForm>[] = React.useMemo(
        () => [
            {
                accessorKey: "client_first_name",
                header: "Client Name",
                cell: ({ row }) => (
                    <Link
                        to={`/clients/${row.original.client_id}`}
                        className="font-medium text-textLink underline"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {displayNameInRightFormat({
                            firstName: row.original.client_first_name,
                            lastName: row.original.client_last_name,
                        })}
                    </Link>
                ),
            },
            {
                accessorKey: "client_email",
                header: "Email",
                cell: ({ row }) => (
                    <span className="truncate max-w-[200px] block">
                        {handleDisplayClientEmailCorrectly(
                            row.original.client_email
                        )}
                    </span>
                ),
            },
            {
                accessorKey: "primary_assignee",
                header: "Care Coordinator(s)",
                cell: ({ row }) => {
                    const coordinators = [
                        row.original.primary_assignee,
                        row.original.secondary_assignee,
                    ]
                        .map((coordinator) =>
                            coordinator?.first_name
                                ? displayNameInRightFormat({
                                      firstName: coordinator.first_name,
                                      lastName: coordinator.last_name ?? "",
                                  })
                                : ""
                        )
                        .filter(Boolean);

                    return coordinators.length ? (
                        <span>{coordinators.join(", ")}</span>
                    ) : (
                        <span className="text-textSec">—</span>
                    );
                },
            },
            {
                accessorKey: "form_type",
                header: "Form Type",
                cell: ({ row }) => (
                    <span className="capitalize">
                        {makeStringFirstLetterCapital(
                            removeEnumUnderscore(row.original.form_type)
                        )}
                    </span>
                ),
            },
            {
                accessorKey: "authorized_recipient",
                header: "Authorized Recipient",
                cell: ({ row }) => {
                    const users = row.original.form_content
                        ?.authorized_users as string[];
                    return users ? (
                        <span>{users.join(",")}</span>
                    ) : (
                        <span className="text-textSec">—</span>
                    );
                },
            },
            {
                accessorKey: "status",
                header: "Status",
                cell: ({ row }) => {
                    const { status } = row.original;
                    if (!status) return <span className="text-textSec">—</span>;
                    if (!canWriteClientForms) {
                        return (
                            <Badge
                                content={getStatusDisplay(status)}
                                className={getStatusTag(status)}
                            />
                        );
                    }
                    return (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton
                                    className={cn(
                                        `capitalize !min-h-7 ![border:none] px-2 ${getStatusTag(
                                            status
                                        )}`
                                    )}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {getStatusDisplay(status)}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent width="auto" align="end">
                                {CLIENT_FORM_STATUSES.filter(
                                    (s) => s !== status
                                ).map((s) => (
                                    <DropdownItem
                                        key={s}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleChangeStatus(row.original, s);
                                        }}
                                    >
                                        {getStatusDisplay(s)}
                                    </DropdownItem>
                                ))}
                            </DropdownContent>
                        </Dropdown>
                    );
                },
            },
            {
                accessorKey: "start_date",
                header: "Start Date",
                cell: ({ row }) => {
                    const isEditing = editingFormId === row.original.form_id;
                    return isEditing ? (
                        <div
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            role="button"
                            tabIndex={0}
                            className="w-32"
                        >
                            <DatePicker
                                selected={editedStartDate}
                                onChange={(date) => setEditedStartDate(date)}
                                placeholderText="MM/DD/YYYY"
                                isClearable
                                isInDialog
                            />
                        </div>
                    ) : (
                        <span>
                            {row.original.start_date
                                ? formatDate(
                                      appendMidnightTimeToDate(
                                          row.original.start_date
                                      ),
                                      true
                                  )
                                : "—"}
                        </span>
                    );
                },
            },
            {
                accessorKey: "end_date",
                header: "End Date",
                cell: ({ row }) => {
                    const isEditing = editingFormId === row.original.form_id;
                    return isEditing ? (
                        <div
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            role="button"
                            tabIndex={0}
                            className="w-32"
                        >
                            <DatePicker
                                selected={editedEndDate}
                                onChange={(date) => setEditedEndDate(date)}
                                placeholderText="MM/DD/YYYY"
                                isClearable
                                isInDialog
                            />
                        </div>
                    ) : (
                        <span>
                            {row.original.end_date
                                ? formatDate(
                                      appendMidnightTimeToDate(
                                          row.original.end_date
                                      ),
                                      true
                                  )
                                : "—"}
                        </span>
                    );
                },
            },
            {
                id: "expiration_status",
                header: "Expiration Status",
                cell: ({ row }) => (
                    <span>
                        {formatExpiryStatus(
                            differenceInCalendarDays(
                                new Date(
                                    appendMidnightTimeToDate(
                                        row.original.end_date
                                    )
                                ),
                                new Date()
                            )
                        )}
                    </span>
                ),
            },
            {
                accessorKey: "signed_at",
                header: "Signed Date",
                cell: ({ row }) => {
                    const isEditing = editingFormId === row.original.form_id;
                    return isEditing ? (
                        <div
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            role="button"
                            tabIndex={0}
                            className="w-32"
                        >
                            <DatePicker
                                selected={editedSignedDate}
                                onChange={(date) => setEditedSignedDate(date)}
                                placeholderText="MM/DD/YYYY"
                                isClearable
                                isInDialog
                            />
                        </div>
                    ) : (
                        <span>
                            {row.original.signed_at
                                ? formatDate(
                                      appendMidnightTimeToDate(
                                          row.original.signed_at
                                      ),
                                      true
                                  )
                                : "—"}
                        </span>
                    );
                },
            },
            {
                accessorKey: "document_links",
                header: "Documents Count",
                cell: ({ row }) => {
                    const documents = row.original.document_links || [];
                    return documents.length > 0 ? (
                        <Button
                            variant="normal"
                            size="auto"
                            className="p-0 underline font-normal text-textLink"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleViewDocuments(documents);
                            }}
                        >
                            {documents.length} document
                            {documents.length !== 1 ? "s" : ""}
                        </Button>
                    ) : (
                        <span className="text-textSec">No documents</span>
                    );
                },
            },
            {
                accessorKey: "updated_at",
                header: "Updated Date",
                cell: ({ row }) => formatDate(row.original.updated_at),
            },
            {
                accessorKey: "actions",
                header: "Actions",
                cell: ({ row }) => {
                    const form = row.original;
                    const isEditing = editingFormId === form.form_id;
                    return (
                        <div className="flex gap-2 items-center">
                            {isEditing ? (
                                <>
                                    <ActionButton
                                        size="auto"
                                        variant="normal"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCancelEdit();
                                        }}
                                        disabled={updateFormMutation.isLoading}
                                        actionTitle="Cancel"
                                    >
                                        <TimesIcon className="stroke-danger size-3.5" />
                                    </ActionButton>
                                    <ActionButton
                                        size="auto"
                                        variant="normal"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSaveEdit(form);
                                        }}
                                        disabled={updateFormMutation.isLoading}
                                        actionTitle="Save"
                                    >
                                        <CheckIcon className="stroke-primary size-3.5" />
                                    </ActionButton>
                                </>
                            ) : (
                                <>
                                    {canWriteClientForms && (
                                        <ActionButton
                                            size="auto"
                                            variant="normal"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditForm(form);
                                            }}
                                            actionTitle="Edit dates"
                                        >
                                            <EditIcon className="stroke-text" />
                                        </ActionButton>
                                    )}
                                    <ActionButton
                                        size="auto"
                                        variant="normal"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedForm(form);
                                            setDialogInView("activity");
                                        }}
                                        actionTitle="View activity log"
                                    >
                                        <EyeIcon className="stroke-text" />
                                    </ActionButton>
                                    {canWriteClientForms && (
                                        <>
                                            <ActionButton
                                                size="auto"
                                                variant="normal"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedForm(form);
                                                    setDialogInView("upload");
                                                }}
                                                actionTitle="Upload documents"
                                            >
                                                <UploadIcon className="stroke-text" />
                                            </ActionButton>
                                            <ActionButton
                                                size="auto"
                                                variant="normal"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedForm(form);
                                                    setDialogInView("delete");
                                                }}
                                                actionTitle="Delete form"
                                            >
                                                <DeleteIcon className="stroke-danger size-3.5" />
                                            </ActionButton>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    );
                },
            },
        ],
        [
            canWriteClientForms,
            editingFormId,
            editedStartDate,
            editedEndDate,
            editedSignedDate,
            updateFormMutation.isLoading,
            handleChangeStatus,
            handleViewDocuments,
            handleSaveEdit,
            handleEditForm,
        ]
    );

    return (
        <>
            <Navbar title="Other Forms" />
            <Dialog
                open={dialogInView === "activity" && Boolean(selectedForm)}
                onOpenChange={() => setDialogInView(null)}
            >
                <DialogContent
                    variant="drawer"
                    title="Activity History"
                    handleCloseDialog={() => setDialogInView(null)}
                    showFooter={false}
                >
                    <FormActivityLog
                        onClose={() => {
                            setDialogInView(null);
                            setSelectedForm(null);
                        }}
                        form={selectedForm as IClientForm}
                        onFormUpdate={(updatedForm) =>
                            setSelectedForm(updatedForm)
                        }
                    />
                </DialogContent>
            </Dialog>

            <Dialog
                open={showDocumentViewer}
                onOpenChange={handleCloseDocumentViewer}
            >
                <DialogContent
                    variant="drawer"
                    title={
                        selectedDocuments.length > 0
                            ? selectedDocuments[currentDocumentIndex]
                                  ?.document_name || "Document"
                            : "Document"
                    }
                    handleCloseDialog={handleCloseDocumentViewer}
                    showFooter={false}
                    wrapperClass="w-[900px]"
                >
                    {selectedDocuments.length > 0 ? (
                        <div>
                            {selectedDocuments.length > 1 && (
                                <div className="flex items-center justify-between mb-4 p-3 bg-[--card-bg-gray] rounded-md">
                                    <Button
                                        variant="secondary"
                                        size="small"
                                        onClick={() =>
                                            setCurrentDocumentIndex((prev) =>
                                                prev > 0
                                                    ? prev - 1
                                                    : selectedDocuments.length -
                                                      1
                                            )
                                        }
                                    >
                                        Previous
                                    </Button>
                                    <span className="text-sm font-medium">
                                        Document {currentDocumentIndex + 1} of{" "}
                                        {selectedDocuments.length}
                                    </span>
                                    <Button
                                        variant="secondary"
                                        size="small"
                                        onClick={() =>
                                            setCurrentDocumentIndex((prev) =>
                                                prev <
                                                selectedDocuments.length - 1
                                                    ? prev + 1
                                                    : 0
                                            )
                                        }
                                    >
                                        Next
                                    </Button>
                                </div>
                            )}
                            <DocumentViewer
                                document={{
                                    document_id:
                                        selectedDocuments[currentDocumentIndex]
                                            ?.document_id,
                                    url: selectedDocuments[currentDocumentIndex]
                                        ?.document_url,
                                    title: selectedDocuments[
                                        currentDocumentIndex
                                    ]?.document_name,
                                    type: "client_form",
                                    metadata: {
                                        content_type:
                                            selectedDocuments[
                                                currentDocumentIndex
                                            ]?.content_type,
                                    },
                                    uploaded_by: {
                                        user_id:
                                            selectedDocuments[
                                                currentDocumentIndex
                                            ]?.uploaded_by || "",
                                        first_name: "",
                                        last_name:
                                            selectedDocuments[
                                                currentDocumentIndex
                                            ]?.uploaded_by || "",
                                    },
                                    uploaded_date:
                                        selectedDocuments[currentDocumentIndex]
                                            ?.uploaded_at,
                                    third_party_type: null,
                                    date_of_service: null,
                                    note_type: null,
                                }}
                                file={
                                    selectedDocuments[currentDocumentIndex]
                                        ?.document_url
                                }
                            />
                        </div>
                    ) : (
                        <div />
                    )}
                </DialogContent>
            </Dialog>

            <Dialog
                open={dialogInView === "create"}
                onOpenChange={() => setDialogInView(null)}
            >
                <DialogContent
                    variant="center"
                    title="Add Form Record"
                    handleCloseDialog={() => setDialogInView(null)}
                    saveText="Add Record"
                    submitBtnFormValue="create-client-form"
                    wrapperClass="w-[500px]"
                >
                    <CreateClientFormForm
                        onFormSubmit={() => setDialogInView(null)}
                    />
                </DialogContent>
            </Dialog>
            <Dialog
                open={Boolean(selectedForm) && dialogInView === "upload"}
                onOpenChange={() => {
                    setDialogInView(null);
                    setSelectedForm(null);
                }}
            >
                <DialogContent
                    variant="center"
                    title="Upload Documents"
                    handleCloseDialog={() => {
                        setDialogInView(null);
                        setSelectedForm(null);
                    }}
                    saveText="Upload"
                    submitBtnFormValue="upload-form-documents"
                    wrapperClass="w-[500px]"
                >
                    <UploadFormDocumentForm
                        clientId={selectedForm?.client_id as string}
                        formId={selectedForm?.form_id as string}
                        onFormSubmit={() => {
                            setDialogInView(null);
                            setSelectedForm(null);
                        }}
                    />
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={dialogInView === "delete" && Boolean(selectedForm)}
            >
                <AlertDialogContent
                    title="Delete Form Record"
                    description={
                        <>
                            Are you sure you want to delete this{" "}
                            <span className="font-medium">
                                {selectedForm?.form_type
                                    ? makeStringFirstLetterCapital(
                                          removeEnumUnderscore(
                                              selectedForm.form_type
                                          )
                                      )
                                    : "form"}{" "}
                            </span>
                            record for{" "}
                            <span>
                                {selectedForm
                                    ? displayNameInRightFormat({
                                          firstName:
                                              selectedForm.client_first_name,
                                          lastName:
                                              selectedForm.client_last_name,
                                      })
                                    : "this client"}
                            </span>
                            ? This action cannot be undone.
                        </>
                    }
                    onSaveClick={handleDeleteForm}
                    onCancelClick={() => {
                        setDialogInView(null);
                        setSelectedForm(null);
                    }}
                    isSaveBtnDisabled={deleteFormMutation.isLoading}
                    isDeleting
                    saveText="Delete"
                />
            </AlertDialog>

            <div className="page">
                <Card classNames="flex flex-col gap-[--spacing-items] mb-[--spacing-header]">
                    <div className="flex justify-between items-center">
                        <SearchInput
                            placeholder="Search by client name or email"
                            containerClass="w-[400px]"
                            onChange={(e) =>
                                handleSetParams("search", e.target.value)
                            }
                            defaultValue={searchString}
                        />
                        {canWriteClientForms && (
                            <Button onClick={() => setDialogInView("create")}>
                                Add Record
                            </Button>
                        )}
                    </div>

                    <div className="flex items-center gap-[--spacing-items] flex-wrap">
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton>
                                    {statusFilter
                                        ? getStatusDisplay(statusFilter)
                                        : "Status"}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent width={180}>
                                <DropdownItem
                                    onClick={() =>
                                        handleSetParams("status", "")
                                    }
                                    isSelected={!statusFilter}
                                    closeMenuOnSelect
                                >
                                    All Statuses
                                </DropdownItem>
                                {CLIENT_FORM_STATUSES.map((s) => (
                                    <DropdownItem
                                        key={s}
                                        onClick={() =>
                                            handleSetParams("status", s)
                                        }
                                        isSelected={statusFilter === s}
                                        closeMenuOnSelect
                                    >
                                        {getStatusDisplay(s)}
                                    </DropdownItem>
                                ))}
                            </DropdownContent>
                        </Dropdown>

                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton>
                                    {formTypeFilter
                                        ? makeStringFirstLetterCapital(
                                              removeEnumUnderscore(
                                                  formTypeFilter
                                              )
                                          )
                                        : "Form Type"}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent width={220}>
                                <DropdownItem
                                    onClick={() =>
                                        handleSetParams("form_type", "")
                                    }
                                    isSelected={!formTypeFilter}
                                    closeMenuOnSelect
                                >
                                    All Types
                                </DropdownItem>
                                {Object.values(FormType).map((ft) => (
                                    <DropdownItem
                                        key={ft}
                                        onClick={() =>
                                            handleSetParams("form_type", ft)
                                        }
                                        isSelected={formTypeFilter === ft}
                                        closeMenuOnSelect
                                    >
                                        {makeStringFirstLetterCapital(
                                            removeEnumUnderscore(ft)
                                        )}
                                    </DropdownItem>
                                ))}
                            </DropdownContent>
                        </Dropdown>

                        <Popover>
                            <PopoverTrigger asChild>
                                <FilterButton>Start Date Range</FilterButton>
                            </PopoverTrigger>
                            <PopoverContent align="end">
                                <div className="mb-1">
                                    <DatePicker
                                        label="From"
                                        placeholderText="MM/DD/YYYY"
                                        selected={startDateFromFilterToDate}
                                        onChange={(date) =>
                                            handleSetParams(
                                                "start_date_from",
                                                date
                                                    ? format(date, "yyyy-MM-dd")
                                                    : ""
                                            )
                                        }
                                        isClearable
                                        isInDialog
                                    />
                                </div>
                                <DatePicker
                                    label="To"
                                    placeholderText="MM/DD/YYYY"
                                    selected={startDateToFilterToDate}
                                    onChange={(date) =>
                                        handleSetParams(
                                            "start_date_to",
                                            date
                                                ? format(date, "yyyy-MM-dd")
                                                : ""
                                        )
                                    }
                                    minDate={startDateFromFilterToDate}
                                    isClearable
                                    isInDialog
                                />
                            </PopoverContent>
                        </Popover>
                    </div>

                    <TableFiltersDisplay
                        appliedFilters={[
                            {
                                key: "status",
                                values: statusFilter
                                    ? [getStatusDisplay(statusFilter)]
                                    : [],
                            },
                        ]}
                        wrapperClass="mt-[--spacing-items]"
                        localStorageKey="clientforms_filters"
                    />
                </Card>

                {error && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error?.response?.data.message ||
                            "Cannot display client forms at this time, please try again later"
                        }
                    />
                )}

                {isLoading && <Loader />}

                {isSuccess && data.data.length === 0 && (
                    <ListState
                        stateHelperText="No form records match your current filters. Try adjusting your search criteria."
                        isError={false}
                        emptyMessage="No client forms found"
                        emptyBtnProps={{
                            showButton: canWriteClientForms,
                            onButtonClick: () => setDialogInView("create"),
                            buttonText: "Add Record",
                        }}
                    />
                )}

                {data?.data && data.data.length > 0 && (
                    <Table
                        columns={columns}
                        data={data.data}
                        hasPagination={data.total_count > limitFilter}
                        showSizeFilter
                        pagination={
                            <Pagination
                                totalCount={data.total_count}
                                totalPages={data.total_pages}
                                currentPage={pageFilter}
                                onPageChange={handlePageChange}
                                limit={limitFilter}
                                onLimitChange={handleLimitChange}
                            />
                        }
                    />
                )}
            </div>
        </>
    );
}
